import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { EntityTypes } from 'constants/entities';
import { restoreState, updateState } from 'core/actions/product/state';
import { updateRequest, createRequest, getRequest } from 'core/services/admin';
import {
  actionRequest,
  dialogueRequest,
  homeRequest,
  moduleRequest,
  tokenRequest,
  verificationRequest,
} from 'core/services/product';
import { IKeyArray } from 'types/IKeyArray';
import { proceed } from './register';
import { restartToken } from './session';
import { IPosition, LoadingState } from 'types/domain/base';

export interface IPlayerReducer {
  timer: ITimerState;
  state: IPlayerState;
  audio: IKeyArray<IAudioState>;
  loading: ILoadingState;
  mode: PlayerMode;
  saving: boolean;
  sceneStates: IKeyArray<ISceneState>;
}

export type IAudioAction = {
  id: number;
  state?: IAudioState;
};

export interface ISceneState {
  id: number;
  load?: LoadingState;
  started?: boolean;
  ended?: boolean;
}

export interface IAudioState {
  muted: boolean;
  paused: boolean;
  volume: number;
}

export enum PortraitState {
  NOT_PORTRAIT = 0,
  TRUE_PORTRAIT = 1,
  SIMULATED_PORTRAIT = 2,
}

export interface IControlsState {
  enabled?: boolean;
  animating?: boolean;
}

export interface ITimerState {
  paused: number;
  start: number;
  delay: number;
  current?: number;
}

export interface ILoadingState {
  app: LoadingState;
  token: LoadingState;
  player: LoadingState;
  course: LoadingState;
  module: LoadingState;
  resource: LoadingState;
  scene: LoadingState;
  action: LoadingState;
  verification: LoadingState;
  dialogue: LoadingState;
  home: LoadingState;
  id?: number;
}

export interface ICameraState {
  zoom: number;
  initialDistance?: number;
  distance?: number;
  save?: boolean;
  position?: IPosition;
}

export interface IViewer {
  state: ViewerState;
  page?: number;
  direction?: ViewerDirection;
}

export enum ViewerState {
  READY = -1,
  RESTING = 0,
  TRANSITIONING = 1,
}

export enum ViewerDirection {
  FORWARD = 1,
  BACK = -1,
}

export type UpdateViewerState = {
  state: ViewerState;
  page: number;
};

export enum VideoPlayState {
  START_PLAYING = 0,
  PLAYING = 1,
  START_PAUSING = 2,
  PAUSED = 3,
  BUFFERING = 4,
  SEEKING = 5,
  ERROR = 6,
}

export interface IVideoState {
  play?: VideoPlayState;
  quality?: number;
  manifest?: any;
  levels?: number[];
  startedInteraction?: boolean;
  interacted?: boolean;
  assetId?: string;
  ready?: boolean;
  ended?: boolean;
  buffering?: boolean;
  seeking?: boolean;
  fallback?: boolean;
}

export enum PlayerMode {
  HOME = 0,
  PREVIEW = 1,
  SCENE = 2,
  ACTION = 3,
  VERIF = 4,
  RESOURCE = 5,
}

export enum DisplayMode {
  STANDARD = 0,
  VR = 1,
  AR = 2,
}

export type IPlayerState = {
  paused: boolean;
  userPaused: boolean;
  faded: boolean;
  portrait: PortraitState;
  video: IVideoState;
  fullscreen: FullScreenState;
  display: DisplayMode;
  camera: ICameraState;
  viewer: IViewer;
  controls: IControlsState;
  xr: boolean;
};

export enum FullScreenState {
  NOT_FULLSCREEN = 1,
  ENTERING_FULLSCREEN = 2,
  FULLSCREEN = 3,
  EXITING_FULLSCREEN = 4,
}

const loading = {
  app: LoadingState.DEFAULT,
  token: LoadingState.DEFAULT,
  course: LoadingState.DEFAULT,
  module: LoadingState.DEFAULT,
  resource: LoadingState.DEFAULT,
  scene: LoadingState.DEFAULT,
  player: LoadingState.DEFAULT,
  action: LoadingState.DEFAULT,
  verification: LoadingState.DEFAULT,
  dialogue: LoadingState.DEFAULT,
  home: LoadingState.DEFAULT,
};

const initialState: IPlayerReducer = {
  timer: {
    paused: 0,
    start: 0,
    delay: 0,
    current: 0,
  },
  state: {
    faded: false,
    paused: true,
    userPaused: false,
    portrait: PortraitState.NOT_PORTRAIT,
    video: {
      play: VideoPlayState.PAUSED,
      quality: 0,
      manifest: null,
      levels: [],
      startedInteraction: false,
      interacted: false,
      assetId: null,
      ready: false,
      ended: false,
      buffering: false,
      seeking: false,
    },
    fullscreen: FullScreenState.NOT_FULLSCREEN,
    display: DisplayMode.STANDARD,
    viewer: {
      state: ViewerState.RESTING,
      page: 0,
    },
    camera: {
      zoom: 1,
    },
    controls: {
      enabled: true,
      animating: false,
    },
    xr: false,
  },
  audio: {},
  loading,
  sceneStates: {},
  saving: false,
  mode: PlayerMode.HOME,
};

const playerSlice = createSlice({
  name: 'player',
  initialState,
  reducers: {
    updatePlayerState: (state, action: PayloadAction<Partial<IPlayerState>>) => {
      state.state = { ...state.state, ...action.payload };
      state = updateTimer(state, action.payload.paused);

      if (state.state.userPaused && !action.payload.userPaused) state.state.userPaused = false;
    },
    updateCameraState: (state, action: PayloadAction<Partial<ICameraState>>) => {
      state.state.camera = { ...state.state.camera, ...action.payload };
    },
    startTimer: (state, action: PayloadAction<number>) => {
      state.timer = {
        paused: new Date().getTime(),
        start: new Date().getTime() - action.payload,
        delay: 0,
        current: action.payload,
      };
    },
    audioState: (state, action: PayloadAction<IAudioAction>) => {
      state.audio[action.payload.id] = { ...(state.audio[action.payload.id] || {}), ...action.payload.state };
    },
    loadingState: (state, action: PayloadAction<Partial<ILoadingState>>) => {
      state.loading = { ...state.loading, ...action.payload };
    },
    updatePlayerMode: (state, action: PayloadAction<PlayerMode>) => {
      state.mode = action.payload;
    },
    updateVideoState: (state, action: PayloadAction<IVideoState>) => {
      state.state.video = { ...state.state.video, ...action.payload };
      if (!!action.payload.buffering) state = updateTimer(state, action.payload.buffering);
    },
    updateViewerState: (state, action: PayloadAction<IViewer>) => {
      state.state.viewer = { ...state.state.viewer, ...action.payload };
    },
    updateSceneState: (state, action: PayloadAction<ISceneState>) => {
      state.sceneStates[action.payload.id] = state.sceneStates[action.payload.id]
        ? { ...state.sceneStates[action.payload.id], ...action.payload }
        : action.payload;
    },
    updateControlsState: (state, action: PayloadAction<IControlsState>) => {
      state.state.controls = { ...state.state.controls, ...action.payload };
    },
    updateXR: (state, action: PayloadAction<boolean>) => {
      state.state.xr = action.payload;
    },
    interact: (state, action: PayloadAction<number>) => {},
  },
  extraReducers: builder => {
    builder.addCase(homeRequest.pending, (state, action) => {
      state.loading.home = LoadingState.LOADING;
    });
    builder.addCase(homeRequest.fulfilled, (state, action) => {
      state.loading.home = LoadingState.LOADED;
    });
    builder.addCase(homeRequest.rejected, (state, action) => {
      state.loading.home = LoadingState.ERROR;
    });
    builder.addCase(tokenRequest.pending, (state, action) => {
      if (state.loading.token !== LoadingState.ERROR) state.loading.token = LoadingState.LOADING;
    });
    builder.addCase(tokenRequest.fulfilled, (state, action) => {
      state.loading.token = LoadingState.LOADED;
    });
    builder.addCase(tokenRequest.rejected, (state, action) => {
      state.loading.token = LoadingState.ERROR;
    });
    builder.addCase(actionRequest.pending, (state, action) => {
      if (state.loading.action !== LoadingState.ERROR) state.loading.action = LoadingState.LOADING;
    });
    builder.addCase(actionRequest.rejected, (state, action) => {
      state.loading.action = LoadingState.ERROR;
    });
    builder.addCase(actionRequest.fulfilled, (state, action) => {
      state.loading.action = LoadingState.LOADED;
    });
    builder.addCase(dialogueRequest.pending, (state, action) => {
      state.loading.dialogue = LoadingState.LOADING;
    });
    builder.addCase(dialogueRequest.rejected, (state, action) => {
      state.loading.dialogue = LoadingState.ERROR;
    });
    builder.addCase(dialogueRequest.fulfilled, (state, action) => {
      state.loading.dialogue = LoadingState.LOADED;
    });
    builder.addCase(moduleRequest.pending, (state, action) => {
      if (state.loading.module !== LoadingState.ERROR) state.loading.module = LoadingState.LOADING;
    });
    builder.addCase(moduleRequest.rejected, (state, action) => {
      state.loading.module = LoadingState.ERROR;
    });
    builder.addCase(moduleRequest.fulfilled, (state, action) => {
      state.loading.module = LoadingState.LOADED;
    });
    builder.addCase(getRequest.pending, (state, action) => {
      if (action.meta.arg.type === EntityTypes.MODULE) state.loading.module = LoadingState.LOADING;
      if (action.meta.arg.type === EntityTypes.COURSE) state.loading.course = LoadingState.LOADING;
    });
    builder.addCase(getRequest.fulfilled, (state, action) => {
      if (action.meta.arg.type === EntityTypes.MODULE) state.loading.module = LoadingState.LOADED;
      if (action.meta.arg.type === EntityTypes.COURSE) state.loading.course = LoadingState.LOADED;
    });

    // builder.addCase('commands/select', (state, action: any) => {
    //   state.state.controls.enabled = !action.payload.some(item => item.type === EntityTypes.ACTION_ENTITY);
    // });
    builder
      .addCase(updateRequest.pending, (state, action) => {
        state.saving = true;
      })
      .addCase(updateRequest.fulfilled, (state, action) => {
        state.saving = false;
      })
      .addCase(createRequest.pending, (state, action) => {
        state.saving = true;
      })
      .addCase(createRequest.fulfilled, (state, action) => {
        state.saving = false;
      })
      .addCase(proceed, (state, action) => {
        state.state.viewer.state = ViewerState.READY;
      })
      .addCase(restoreState, state => {
        state.loading = loading;
      });
  },
});

export const {
  updateVideoState,
  updateViewerState,
  updatePlayerState,
  updateCameraState,
  updateControlsState,
  updateXR,
  startTimer,
  audioState,
  loadingState,
  updateSceneState,
  updatePlayerMode,
  interact,
} = playerSlice.actions;
export default playerSlice.reducer;

function updateTimer(state, paused) {
  return !!paused
    ? {
        ...state,
        timer: { paused: new Date().getTime() },
      }
    : {
        ...state,
        timer: {
          delay: (state.delay += state.paused ? new Date().getTime() - state.paused : 0),
          paused: null,
        },
      };
}
